import {useTranslation} from 'react-i18next'

import lock from 'assets/lock.svg'
import mail from 'assets/mail.svg'
import person from 'assets/person.svg'
import building from 'assets/building.svg'
import telephone from 'assets/telephone.svg'

import {Country} from 'features/common/core/entitites'

import Button from 'features/common/components/Button'
import FormInput from '../common/components/FormInput'
import FormField from '../common/components/FormField'
import CountrySelect from './components/CountrySelect'
import Checkbox from 'features/common/components/Checkbox'
import StepProgressBar from 'features/common/components/StepProgressBar'
import PasswordFormInput from '../common/components/PasswordFormInput'
import {useTypedTranslation} from 'i18n'
import useSignup from '../../../common/hooks/useSignup'

export function Signup() {
    const {t} = useTranslation()
    const {t: typedT} = useTypedTranslation()
    const {data, errors, onChange, onSubmit, onSubmitError, loading} =
        useSignup()
    const onChangeCountry = (v: string | number) => {
        onChange({prop: 'country', value: v.toString() as Country})
    }
    const onChangeInput =
        (prop: keyof typeof data) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange({prop, value: e.target.value.toString()})
        }

    const onChangeTOS = () => {
        onChange({prop: 'tos', value: !data.tos})
    }
    const noErrors = Object.keys(errors).length === 0
    const noEmptyFields = !!(
        data.name &&
        data.organization &&
        data.country &&
        data.phone &&
        data.email &&
        data.password &&
        data.tos
    )

    const disableSubmit = !(noErrors && noEmptyFields)
    return (
        <form
            className="flex w-[36.5rem] flex-col items-stretch justify-center gap-10"
            onSubmit={(e) => e.preventDefault()}
        >
            <div className="w-full text-left text-[40px]">
                {t('AUTH_SIGNUP_TITLE')}
            </div>
            <div className="flex min-w-0 flex-1 flex-col items-stretch justify-center gap-7.5">
                <FormField title={t('AUTH_NAME')}>
                    <FormInput
                        icon={person}
                        iconAlt="Name"
                        error={errors.name}
                        inputProps={{
                            value: data.name,
                            onChange: onChangeInput('name'),
                            placeholder: t('AUTH_NAME_PLACEHOLDER'),
                            name: 'name',
                            autoComplete: 'name',
                        }}
                    />
                </FormField>
                <FormField title={t('AUTH_ORGANIZATION')}>
                    <FormInput
                        icon={building}
                        iconAlt="Organization"
                        error={errors.organization}
                        inputProps={{
                            value: data.organization,
                            onChange: onChangeInput('organization'),
                            placeholder: t('AUTH_ORGANIZATION_PLACEHOLDER'),
                            type: 'text',
                            name: 'organization',
                            autoComplete: 'organization',
                        }}
                    />
                </FormField>
                <FormField title={t('AUTH_COUNTRY')}>
                    <CountrySelect
                        chosenValue={data.country}
                        onChange={onChangeCountry}
                    />
                </FormField>
                <FormField title={t('AUTH_PHONE')}>
                    <FormInput
                        icon={telephone}
                        iconAlt="Phone"
                        error={errors.phone}
                        inputProps={{
                            value: data.phone,
                            onChange: onChangeInput('phone'),
                            placeholder: t('AUTH_PHONE_PLACEHOLDER'),
                            // type: 'number',
                            // pattern: '+d*',
                            // inputMode: 'tel',
                            // name: 'tel',
                            // autoComplete: 'tel',
                        }}
                    />
                </FormField>
                <FormField title={t('AUTH_EMAIL')}>
                    <FormInput
                        icon={mail}
                        iconAlt="E-mail"
                        error={errors.email}
                        inputProps={{
                            value: data.email,
                            onChange: onChangeInput('email'),
                            placeholder: t('AUTH_EMAIL_PLACEHOLDER'),
                            type: 'email',
                            name: 'email',
                            autoComplete: 'email',
                        }}
                    />
                </FormField>
                <FormField title={t('AUTH_PASSWORD')}>
                    <PasswordFormInput
                        icon={lock}
                        iconAlt="Password"
                        error={errors.password}
                        inputProps={{
                            value: data.password,
                            onChange: onChangeInput('password'),
                            placeholder: t('AUTH_SIGNUP_PASSWORD_PLACEHOLDER'),
                            type: 'password',
                            name: 'current-password',
                            autoComplete: 'current-password',
                        }}
                    />
                </FormField>
            </div>
            {onSubmitError && (
                <div className="pl-4 text-xs text-red-700">
                    {typedT(onSubmitError.message)}
                </div>
            )}
            <div className="flex w-full justify-between gap-5">
                <div className="flex items-center gap-3 text-sm">
                    <div>
                        <Checkbox value={data.tos} onChange={onChangeTOS} />
                    </div>
                    {t('AUTH_STRIPE_TOS')}
                </div>
                <div>
                    <Button
                        className="whitespace-nowrap rounded-full py-4 px-16"
                        onClick={onSubmit}
                        disabled={disableSubmit}
                        type="submit"
                        isLoading={loading}
                    >
                        {t('AUTH_SIGNUP_BUTTON')}
                    </Button>
                </div>
            </div>
            <StepProgressBar
                points={[
                    {
                        title: t('ONBOARDING_STEP_REGISTRATION'),
                        status: 'current',
                    },
                    {title: t('ONBOARDING_STEP_EMAIL'), status: 'empty'},
                    {title: t('ONBOARDING_STEP_STRIPE'), status: 'empty'},
                ]}
            />
        </form>
    )
}

export default Signup
